import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { FiPlus, FiSearch, FiSend, FiUsers } from "react-icons/fi";
import { HiOutlineCheckCircle, HiOutlineExclamationCircle } from "react-icons/hi";
import { GoMail } from "react-icons/go";
import CampaignCard from "./CampaignCard";
import NewCampaignModal from "./NewCampaignModal";
import CampaignManageModal from "./CampaignManageModal";
import CampaignDetailDrawer from "./CampaignDetailDrawer";
import ConfirmDialog from "../../../components/ConfirmDialog";

const FILTERS = [
  { id: "all", label: "Todas" },
  { id: "draft", label: "Rascunho" },
  { id: "scheduled", label: "Agendada" },
  { id: "sent", label: "Enviada" },
  { id: "failed", label: "Falhou" },
];

export default function CampaignsTab({ data }) {
  const {
    campaigns,
    lists,
    createCampaign,
    updateCampaign,
    deleteCampaign,
    sendCampaign,
    sendTestCampaign,
    scheduleCampaign,
    unscheduleCampaign,
    getListContactsCount,
    fetchCampaignTemplate,
    fetchCampaignEvents,
  } = data;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [newModalOpen, setNewModalOpen] = useState(false);
  const [manageCampaignId, setManageCampaignId] = useState(null);
  const [detailCampaign, setDetailCampaign] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const totalCampaigns = campaigns.length;
  const sentCount = campaigns.filter((c) => c.status === "sent").length;
  const draftCount = campaigns.filter((c) => c.status === "draft" || c.status === "scheduled").length;
  const totalRecipients = campaigns
    .filter((c) => c.status === "sent")
    .reduce((sum, c) => sum + (c.recipientsCount || 0), 0);

  const listNameById = useMemo(
    () => Object.fromEntries(lists.map((l) => [l.id, l.name])),
    [lists]
  );

  const filtered = campaigns.filter((c) => {
    const matchSearch = c.name.toLowerCase().includes(searchTerm.toLowerCase());
    if (statusFilter === "all") return matchSearch;
    return matchSearch && c.status === statusFilter;
  });

  // Campanhas em draft/scheduled/failed abrem o modal de gerenciamento
  // (editar + preview + enviar teste + disparar + agendar). Campanhas já
  // enviadas abrem o drawer de relatório (CampaignDetailDrawer).
  const handleOpenCampaign = (campaign) => {
    if (campaign.status === "sent") {
      setDetailCampaign(campaign);
    } else {
      setManageCampaignId(campaign.id);
    }
  };

  const manageCampaign = campaigns.find((c) => c.id === manageCampaignId) || null;

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteCampaign(deleteTarget.id);
      toast.success("Campanha excluída");
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Erro ao excluir campanha");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="kpi-cards">
        <div className="kpi-card">
          <div className="card-content">
            <div className="thumb"><FiSend /></div>
            <div className="text">
              <p>{totalCampaigns}</p>
              <p>Total de Campanhas</p>
            </div>
          </div>
        </div>
        <div className="kpi-card success">
          <div className="card-content">
            <div className="thumb"><HiOutlineCheckCircle /></div>
            <div className="text">
              <p>{sentCount}</p>
              <p>Enviadas</p>
            </div>
          </div>
        </div>
        <div className="kpi-card warning">
          <div className="card-content">
            <div className="thumb"><HiOutlineExclamationCircle /></div>
            <div className="text">
              <p>{draftCount}</p>
              <p>Rascunhos / Agendadas</p>
            </div>
          </div>
        </div>
        <div className="kpi-card info">
          <div className="card-content">
            <div className="thumb"><FiUsers /></div>
            <div className="text">
              <p>{totalRecipients}</p>
              <p>Destinatários Alcançados</p>
            </div>
          </div>
        </div>
      </div>

      <div className="toolbar">
        <div className="toolbar-left">
          <button className="btn-new" onClick={() => setNewModalOpen(true)}>
            <FiPlus /> Nova Campanha
          </button>
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Buscar campanha..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div className="toolbar-right">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`filter-pill ${statusFilter === f.id ? "active" : ""}`}
              onClick={() => setStatusFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="main-content">
        {filtered.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><GoMail /></div>
            <h4>
              {searchTerm || statusFilter !== "all"
                ? "Nenhuma campanha encontrada"
                : "Nenhuma campanha criada"}
            </h4>
            <p>
              {searchTerm || statusFilter !== "all"
                ? "Ajuste os filtros ou o termo de busca"
                : "Crie sua primeira campanha de email marketing"}
            </p>
            {!searchTerm && statusFilter === "all" && (
              <button className="empty-cta" onClick={() => setNewModalOpen(true)}>
                <FiPlus /> Nova Campanha
              </button>
            )}
          </div>
        ) : (
          <div className="campaign-grid">
            {filtered.map((campaign) => (
              <CampaignCard
                key={campaign.id}
                campaign={campaign}
                listName={listNameById[campaign.listId]}
                onContinueEdit={handleOpenCampaign}
                onSend={handleOpenCampaign}
                onViewDetails={handleOpenCampaign}
                onDelete={setDeleteTarget}
              />
            ))}
          </div>
        )}
      </div>

      <NewCampaignModal
        isOpen={newModalOpen}
        onClose={() => setNewModalOpen(false)}
        lists={lists}
        getListContactsCount={getListContactsCount}
        onCreateDraft={createCampaign}
      />

      {manageCampaign && (
        <CampaignManageModal
          campaign={manageCampaign}
          lists={lists}
          getListContactsCount={getListContactsCount}
          onClose={() => setManageCampaignId(null)}
          onUpdate={updateCampaign}
          onSendTest={sendTestCampaign}
          onSend={sendCampaign}
          onSchedule={scheduleCampaign}
          onUnschedule={unscheduleCampaign}
          fetchTemplate={fetchCampaignTemplate}
          onDelete={(campaign) => {
            setManageCampaignId(null);
            setDeleteTarget(campaign);
          }}
        />
      )}

      {detailCampaign && (
        <CampaignDetailDrawer
          campaign={detailCampaign}
          listName={listNameById[detailCampaign.listId]}
          onClose={() => setDetailCampaign(null)}
          fetchTemplate={fetchCampaignTemplate}
          fetchEvents={fetchCampaignEvents}
          onDelete={(campaign) => {
            setDetailCampaign(null);
            setDeleteTarget(campaign);
          }}
        />
      )}

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title="Excluir campanha"
        message={
          deleteTarget?.status === "sent"
            ? `Esta campanha já foi enviada para ${deleteTarget?.recipientsCount || 0} destinatário(s) e tem estatísticas de abertura/clique. Excluir vai apagar permanentemente a campanha e todo o histórico de rastreamento associado. Os emails já enviados não serão desfeitos. Deseja continuar?`
            : `Tem certeza que deseja excluir a campanha "${deleteTarget?.name}"? Essa ação não pode ser desfeita.`
        }
        confirmText={deleting ? "Excluindo..." : "Excluir"}
        cancelText="Cancelar"
        type="danger"
      />
    </>
  );
}
